'use client';
import { useTranslations } from 'next-intl';
import type { FC } from 'react';
import { AgentLineScript } from '@/app/components/icons/AgentLineScript';
import { BaseLineMore } from '@/app/components/icons/BaseLineMore';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import HoverPlayMedia from '@/app/components/shared/HoverPlayMedia';
import { Button } from '@/app/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/app/components/ui/dropdown-menu';
import { useDeleteElements } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { PostReferenceItem } from '@/app/types';
import { cn, convertViduV1ElementToElementType, getAspectRatio, reportEvent } from '@/app/utils';

interface PostReferenceCardProps {
  data: PostReferenceItem;
  onClick?: () => void;
  className?: string;
  onDelete?: (ids: string[]) => void;
}

export const PostReferenceCard: FC<PostReferenceCardProps> = ({ data, onClick, className, onDelete }) => {
  const t = useTranslations();

  const creation = data.share?.creations?.[0];
  const coverUri = creation?.cover_uri;
  const videoUri = creation?.uri;

  const aspectRatio = getAspectRatio({
    size: creation?.resolution,
  });

  const { openReferenceModalWithData, userInfo } = useStore((state) => ({
    openReferenceModalWithData: state.openReferenceModalWithData,
    userInfo: state.userInfo,
  }));

  const { handleDelete } = useDeleteElements({
    onSuccess: onDelete,
  });

  const goToDelete = () => {
    if (!data.element) return;

    reportEvent('element_operation', {
      operation_type: 'delete',
    });

    const elementType = convertViduV1ElementToElementType(data.element);
    handleDelete(elementType);
  };

  const isOwnElement = data.element?.type === 'user' && data.element?.creator_id === userInfo?.id;

  return (
    <div
      className={cn('group relative w-full cursor-pointer overflow-hidden rounded-4', className)}
      onClick={onClick}
      style={{
        aspectRatio,
      }}
    >
      {videoUri && coverUri && (
        <HoverPlayMedia
          id={data.element?.id || ''}
          uri={videoUri}
          coverUri={coverUri}
          className="h-full w-full"
          aspectRatio={aspectRatio}
          videoClassName={'object-cover'}
          borderRadiusClassName="rounded-4"
          eventName="post_reference_card_hover"
        />
      )}
      <div className="absolute right-0 bottom-0 left-0 flex items-center gap-2 bg-[linear-gradient(180deg,rgba(0,0,0,0.00)_0%,rgba(0,0,0,0.64)_100%)] p-3 text-sm leading-[1.4]">
        <div className="flex-1 overflow-hidden">
          <CustomTooltip content={data.element?.name || ''} maxLines={1} showOnlyOnOverflow={true}>
            {data.element?.name || ''}
          </CustomTooltip>
        </div>
        <div className="flex flex-shrink-0 items-center gap-1">
          <div
            className="group/view cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              openReferenceModalWithData?.({
                referenceModalOpen: true,
                reference: data,
                referenceModalMode: 'view',
                referenceModalFrom: 'select',
                referenceModalFromPage: 'reference_tool',
              });
              reportEvent('element_operation', {
                operation_type: 'view_detail',
              });
            }}
          >
            <AgentLineScript className="size-4 text-system-white80 transition-colors group-hover/view:text-system-white" />
          </div>
          {isOwnElement && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="text"
                  size="original"
                  className="group size-4 p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <BaseLineMore className="size-4 text-system-white80 transition-colors group-hover:text-system-white" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  className="min-w-[126px] hover:bg-[#515765]"
                  onClick={(e) => {
                    e.stopPropagation();
                    goToDelete();
                  }}
                >
                  {t('elements.delete')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </div>
  );
};
